import { SwaggerlessContext } from 'Context/SwaggerlessContext';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withHousehold } from 'Context/HouseholdContext';
import Input from 'Components/UI/BehavioralFormElement/Text/Input';
import Select from 'Components/UI/BehavioralFormElement/Select';
import Icon from 'Components/UI/Icon';
import { householdPlansUri } from 'Consts/advisorUris';
import { savePlan } from 'Helpers/ExternalPlan';
import { setPlan } from 'State/actions';
import LoadingOverlay from 'Components/UI/LoadingOverlay/LoadingOverlay';
import { displayErrorMessage } from 'Helpers/FacetNotificationManager';
import Button from 'Components/UI/Button';
import { getOptions, getFieldDefByName } from 'Helpers/Meta';

import './index.css';
import { Link } from 'react-router-dom';
import MultipicklistSelector from '../MultipicklistSelector';

const mapStateToProps = state => ({
  plan: state.plan,
});

const mapDispatchToProps = dispatch => ({
  set: plan => dispatch(setPlan(plan)),
});

class Start extends Component {
  static handleFailure(error) {
    if (error && error.response && error.response.body && error.response.body.errors) {
      displayErrorMessage(error, error.response.body.errors);
    } else {
      displayErrorMessage(error, 'An unknown error occurred');
    }
  }

  constructor(props) {
    super(props);

    this.handleContinue = this.handleContinue.bind(this);
    this.handleUpdate = this.handleUpdate.bind(this);
    this.updatePickedOptions = this.updatePickedOptions.bind(this);
    this.handleAllowContinue = this.handleAllowContinue.bind(this);
    this.handleContinueBlank = this.handleContinueBlank.bind(this);

    this.state = {
      plan: props.plan,
      loading: false,
      templateOptions: [],
      templateIsExclusive: false,
      pickedOptions: [],
      iconSrc: '',
      externalGoalOptions: undefined,
      defaultOptions: [],
      planChildOptions: null,
    };
  }

  componentDidMount() {
    const { householdId } = this.props;
    const { plan } = this.state;
    const { fetchMeta } = this.context;
    const apiOptions = {
      householdId,
      type: plan.externalPlanType,
    };

    fetchMeta('externalPlan', apiOptions)
      .then(({ data: planMeta }) => {
        const resultTemplate = getFieldDefByName(
          planMeta.attributes.template,
          'templates',
        );
        const isExclusive = resultTemplate.type === 'picklist';
        this.setState({ templateIsExclusive: isExclusive });

        const templateOptions = resultTemplate.options;
        if (templateOptions != null) {
          this.setState({ templateOptions });
        }

        const selectAllTemplateMeta = getFieldDefByName(
          planMeta.attributes.template,
          'selectAllTemplates',
        );
        if (selectAllTemplateMeta.default === 'true' && templateOptions != null) {
          this.setState({ defaultOptions: templateOptions });
        }

        const resultIcons = getOptions(planMeta.attributes.template, 'icon');
        const iconSrc = resultIcons.find(
          item => item.id === plan.externalPlanType,
        ).value;
        this.setState({ iconSrc });

        //  getOptions() prepends a default option.
        //  Only set the state for this if we actually got options back from server for this plan type.
        const externalGoalOptions = getOptions(planMeta.attributes.template, 'externalGoal', 'Please Select A Goal');
        if (externalGoalOptions.length > 1) {
          externalGoalOptions[0].id = 'No Goal Selected';
          plan.externalGoal = externalGoalOptions[0].id;
          this.setState({ externalGoalOptions });
        }

        const planChildOptions = getOptions(planMeta.attributes.template, 'children', 'Select the child');
        if (planChildOptions.length > 1) {
          this.setState({ planChildOptions });
        }

        return Promise.resolve();
      })
      .catch((err) => {
        displayErrorMessage(err, 'Failed to fetch plan meta data');
      });
  }

  handleContinue() {
    const {
      set, householdId,
    } = this.props;
    const {
      plan, pickedOptions,
    } = this.state;
    const { createExternalPlan } = this.context;
    const next = householdPlansUri(householdId);
    this.setState({ loading: true });
    plan.templates = pickedOptions;

    return savePlan(
      (p) => createExternalPlan(p.attributes.householdID, p.type, p),
      plan,
      set,
      () => {},
      next,
      Start.handleFailure,
    ).finally(() => {
      this.setState({ loading: false });
    });
  }

  handleUpdate(field, value) {
    const { plan } = this.state;
    plan[field] = value;
    this.setState(plan);
  }

  updatePickedOptions(newValue) {
    this.setState({ pickedOptions: newValue });
  }

  handleAllowContinue() {
    const { plan, templateOptions, pickedOptions } = this.state;

    const hasName = Boolean(plan.planName);
    if (!hasName) {
      return false;
    }

    if (templateOptions.length > 0 && pickedOptions.length < 1) {
      return false;
    }

    return true;
  }

  handleContinueBlank() {
    this.setState({ pickedOptions: [] }, () => this.handleContinue());
  }

  render() {
    const {
      handleContinue,
      handleUpdate,
      updatePickedOptions,
      handleAllowContinue,
      handleContinueBlank,
      state: { loading },
    } = this;
    const { householdId } = this.props;
    const {
      plan,
      templateOptions,
      templateIsExclusive,
      iconSrc,
      externalGoalOptions,
      defaultOptions,
      planChildOptions,
    } = this.state;
    const { planName, externalGoal, childID } = plan;
    const label = plan.externalPlanLabel;
    return (
      <>
        {loading && <LoadingOverlay />}

        <div className={'PlanStartTemplate'.trim()}>
          <div className="back">
            <Link to={householdPlansUri(householdId)}>
              <span className="fa fa-angle-left" />
              <span>Plan Builder</span>
            </Link>
          </div>
          <div className="header-block">
            <Icon src={iconSrc} />
            <span>{`${label} Plan`}</span>
          </div>

          <div className="questionSet">
            <div className="question">
              How would you like to save this plan?
            </div>
            <div>
              <Input
                name="planName"
                onChange={handleUpdate}
                label="Plan Name"
                placeholder="Plan Name"
                value={planName}
              />
            </div>
            {externalGoalOptions && (
              <div>
                <Select
                  options={externalGoalOptions}
                  name="externalGoal"
                  value={externalGoal}
                  onChange={handleUpdate}
                />
              </div>
            )}
          </div>
          {planChildOptions && (
            <Select
              options={planChildOptions}
              name="childID"
              value={childID}
              onChange={handleUpdate}
            />
          )}
          {templateOptions.length > 0 && (
            <Button
              color="accentBlue"
              disableElevation
              onClick={handleContinueBlank}
              variant="contained"
              fullWidth
              disabled={!planName}
            >
              {' '}
              Create a blank plan
            </Button>
          )}

          <MultipicklistSelector
            onChangePickedOptions={updatePickedOptions}
            planType={plan.externalPlanType}
            templateOptions={templateOptions}
            isExclusive={templateIsExclusive}
            defaultOptions={defaultOptions}
          />
          <Button
            color="secondary"
            disableElevation
            onClick={handleContinue}
            variant="contained"
            fullWidth
            disabled={!handleAllowContinue()}
          >
            Create plan
          </Button>
        </div>
      </>
    );
  }
}

Start.contextType = SwaggerlessContext;

Start.propTypes = {
  plan: PropTypes.object.isRequired,
  set: PropTypes.func.isRequired,
  householdId: PropTypes.number.isRequired,
};

export default withHousehold(connect(mapStateToProps, mapDispatchToProps)(Start));
